import { Component, OnDestroy, OnInit } from "@angular/core";
import { MenuItem } from 'primeng/api';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ProfilService } from '../../services/profil-service';
import { BaseComponent } from 'src/app/base/base-component';
import { AppMainComponent } from "src/app/app.main.component";
import { DonneesBaseService } from "src/app/services/donnees-base-service";
import { BreadcrumbService } from "src/app/app.breadcrumb.service";
import { filter, Subscription } from 'rxjs';

@Component({
    selector: 'app-admin-menu',
    templateUrl: './admin-menu.component.html',
    styles: [`
        :host ::ng-deep .p-panelmenu-panel .p-menuitem-link-active {
            background-color: #e9ecef;
            color: #495057;
            font-weight: bold;

            [data-theme="dark"] & {
                background-color: #262626;
                color: #ffffff;
            }
        }

        :host ::ng-deep .p-panelmenu .p-menuitem-link-active {
            position: relative;
        }
        :host ::ng-deep .p-panelmenu .p-menuitem-link-active::after {
            content: '\\003E';
            /* Unicode rightwards arrow */
            position: absolute;
            right: 0.5rem;
            top: 50%;
            transform: translateY(calc(-50% - 0.15rem));
            color: #1976D2;
            font-size: 1.3em;
            font-weight: bold;
            pointer-events: none;
            [data-theme="dark"] & {
                color: #90caf9;
            }
        }
        
        :host ::ng-deep .p-panelmenu .p-menuitem-link-active .p-menuitem-icon {
            color: #1976D2;
        }
    `],
})
export class AdminMenuComponent extends BaseComponent implements OnInit, OnDestroy {

    menu: MenuItem[];
    private routerSubscription: Subscription;
    sidebarVisible: boolean = false;

    constructor(
        public routerService: Router,
        public profilService: ProfilService,
        public route: ActivatedRoute,
        public appMain: AppMainComponent,
        public sdb: DonneesBaseService,
        public breadcrumbService: BreadcrumbService
    ) {
        super(sdb, profilService, route, routerService, appMain);

        this.breadcrumbService.setItems([
            { label: 'Administration', routerLink: '/admin' }
        ]);
    }

    ngOnInit() {
        this.initialiserMenu();
        this.updateActiveMenuItem();
        
        // Subscribe to router events to update active menu item on navigation
        this.routerSubscription = this.routerService.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => {
                this.updateActiveMenuItem();
                this.sidebarVisible = false; // Close sidebar on navigation
            });
    }

    ngOnDestroy() {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
        }
    }

    initialiserMenu() {
        this.menu = [
            {
                label: 'Général',
                icon: 'pi pi-fw pi-cog',
                command: () => this.navigateAndCloseSidebar('/admin/general'),
                visible: this.profilService.estAutoriseTache('admin_general'),
            },
            {
                label: 'Attributs',
                icon: 'pi pi-fw pi-cog',
                command: () => this.navigateAndCloseSidebar('/admin/attributs'),
                visible: this.profilService.estAutoriseTache('admin_attributs'),
            },
            {
                label: 'Catégories',
                icon: 'pi pi-fw pi-cog',
                command: () => this.navigateAndCloseSidebar('/admin/categories'),
                visible: this.profilService.estAutoriseTache('admin_categories')
            },
            {
                label: 'Dossiers de documents',
                icon: 'pi pi-fw pi-cog',
                command: () => this.navigateAndCloseSidebar('/admin/typeLienDocuments'),
                visible: this.profilService.estAutoriseTache('admin_categories'),
                disabled: true
            },
            {
                label: 'Boites d\'outils',
                icon: 'pi pi-fw pi-cog',
                command: () => this.navigateAndCloseSidebar('/admin/boitesOutil'),
                visible: this.profilService.estAutoriseTache('admin_boites_outils')
            },
            {
                label: 'Listes d\'achat',
                icon: 'pi pi-fw pi-cog',
                command: () => this.navigateAndCloseSidebar('/admin/modelesListeAchat'),
                visible: this.profilService.estAutoriseTache('admin_listes_achats')
            },
            {
                label: 'Listes de vérification',
                icon: 'pi pi-fw pi-cog',
                command: () => this.navigateAndCloseSidebar('/admin/listesverification'),
                visible: this.profilService.estAutoriseTache('temp_mec')
            },
            {
                label: 'Employés',
                icon: 'pi pi-fw pi-users',
                command: () => this.navigateAndCloseSidebar('/admin/employes'),
                visible: this.profilService.estAutoriseTache('admin_employes')
            },
            {
                label: 'Rôles',
                icon: 'pi pi-fw pi-shield',
                command: () => this.navigateAndCloseSidebar('/admin/roles'),
                visible: this.profilService.estAutoriseTache('admin_roles')
            },
            {
                label: 'Clés API',
                icon: 'pi pi-fw pi-key',
                command: () => this.navigateAndCloseSidebar('/admin/clesApi'),
                visible: this.profilService.estAutoriseTache('admin_cles_api')
            },
            {
                label: 'Tâches planifiées',
                icon: 'pi pi-fw pi-cog',
                command: () => this.navigateAndCloseSidebar('/admin/jobs'),
                visible: this.profilService.estAutoriseTache('admin_jobs')
            }
        ];
    }

    /**
     * Navigates to a route and closes the sidebar (useful for mobile)
     */
    navigateAndCloseSidebar(route: string) {
        this.routerService.navigate([route]);
        this.sidebarVisible = false;
    }

    /**
     * Toggles the sidebar visibility
     */
    toggleSidebar() {
        this.sidebarVisible = !this.sidebarVisible;
    }

    /**
     * Updates the active state of menu items based on the current URL
     */
    updateActiveMenuItem() {
        const currentUrl = this.routerService.url;
        
        this.menu.forEach(item => {
            // Extract the route from the command function if it exists
            const route = this.getRouteFromMenuItem(item);
            
            if (route && currentUrl.includes(route)) {
                item.styleClass = 'p-menuitem-link-active';
            } else {
                item.styleClass = '';
            }
        });
    }

    /**
     * Extracts the route path from a menu item's command function
     */
    private getRouteFromMenuItem(item: MenuItem): string | null {
        // Map menu items to their routes based on the command navigation
        const routeMap: { [key: string]: string } = {
            'Général': '/admin/general',
            'Attributs': '/admin/attributs',
            'Catégories': '/admin/categories',
            'Dossiers de documents': '/admin/typeLienDocuments',
            'Boites d\'outils': '/admin/boitesOutil',
            'Listes d\'achat': '/admin/modelesListeAchat',
            'Listes de vérification': '/admin/listesverification',
            'Employés': '/admin/employes',
            'Rôles': '/admin/roles',
            'Clés API': '/admin/clesApi',
            'Tâches planifiées': '/admin/jobs'
        };
        
        return routeMap[item.label] || null;
    }
}
